import axios from "axios";

import { api, endpoints } from "api/api";

import { DocumentTypeApi } from "./documentTypesApi";

// Client of the "Dokumenty 2.0" (pdfme) endpoints. The types mirror the C# DTOs (camelCase on the wire).
// Dates: `lastModified` comes from a MySQL DateTime without a Z, so the response interceptor turns it into a Date;
// the issue dates carry a Z (the API marks them UTC) and therefore stay ISO strings.

export interface DocumentTemplateV2ListItem {
  id: number;
  name: string;
  type: DocumentTypeApi;
  lastModified: Date;
  courseDocumentCount: number;
}

export interface DocumentTemplateV2Usage {
  /** Non-deleted course documents built on the template. */
  courseDocumentCount: number;
  /** Sorted union of the field names those documents saved a value for. */
  fieldNamesWithValues: string[];
}

export interface DocumentTemplateV2 {
  id: number;
  name: string;
  type: DocumentTypeApi;
  /** The pdfme template, JSON-encoded. */
  templateJson: string;
  pdfmeVersion: string;
  lastModified: Date;
  usage: DocumentTemplateV2Usage;
}

/** Body of create and update. `templateJson` never holds `data:` images, only `pazia-asset:` refs. */
export interface DocumentTemplateV2Payload {
  name: string;
  documentTypeId: number;
  templateJson: string;
  pdfmeVersion: string;
}

/** Body of creating and updating a v2 course document. `valuesJson` is a JSON-encoded object of strings. */
export interface CourseDocumentV2Payload {
  name: string;
  templateV2Id: number;
  valuesJson: string;
}

export interface CourseDocumentV2Issue {
  number: number;
  /** ISO date marked UTC (ends with Z), so it is not converted to a Date. */
  issuedDate: string;
  /** ISO date marked UTC, null for a document that never expires. */
  validUntil: string | null;
}

export interface CourseDocumentV2Student {
  studentId: number;
  firstName: string;
  surname: string;
  email: string;
  /** Every text variable (`student.imie`, `dokument.numer`, ...), "" when unknown. */
  variables: Record<string, string>;
  /** Whether {@link documentsV2Api.fetchStudentPhotoDataUrl} has an image for this student. */
  hasPhoto: boolean;
  /** Null until a number was allocated for this student on this document. */
  issue: CourseDocumentV2Issue | null;
  /** The student's own saved pdfme inputs (a JSON-encoded object of strings), null when none. */
  inputsJson: string | null;
}

const MEDIA_TYPE_FALLBACK = "image/png";

const mediaTypeOf = (headers: unknown): string => {
  const raw = (headers as Record<string, unknown> | undefined)?.["content-type"];
  const mediaType = typeof raw === "string" ? raw.split(";")[0].trim().toLowerCase() : "";
  return mediaType || MEDIA_TYPE_FALLBACK;
};

// Chunked: String.fromCharCode(...bytes) on a multi-megabyte photo overflows the call stack.
const toDataUrl = (data: ArrayBuffer, mediaType: string): string => {
  const bytes = new Uint8Array(data);
  const chunkSize = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return `data:${mediaType};base64,${btoa(binary)}`;
};

const list = async (search?: string): Promise<DocumentTemplateV2ListItem[]> => {
  const trimmed = search?.trim();
  const res = await api.get<DocumentTemplateV2ListItem[]>(endpoints.DOCUMENT_TEMPLATES_V2.LIST, {
    params: trimmed ? { search: trimmed } : {},
  });
  return res.data;
};

const get = async (id: number): Promise<DocumentTemplateV2> => {
  const res = await api.get<DocumentTemplateV2>(endpoints.DOCUMENT_TEMPLATES_V2.BY_ID(id));
  return res.data;
};

const create = async (payload: DocumentTemplateV2Payload): Promise<number> => {
  const res = await api.post<number>(endpoints.DOCUMENT_TEMPLATES_V2.LIST, payload);
  return res.data;
};

const update = async (id: number, payload: DocumentTemplateV2Payload): Promise<void> => {
  await api.put(endpoints.DOCUMENT_TEMPLATES_V2.BY_ID(id), payload);
};

const remove = async (id: number): Promise<void> => {
  await api.delete(endpoints.DOCUMENT_TEMPLATES_V2.BY_ID(id));
};

/** Copies a template ("<name> (kopia)"); resolves with the id of the copy. */
const duplicate = async (id: number): Promise<number> => {
  const res = await api.post<number>(endpoints.DOCUMENT_TEMPLATES_V2.DUPLICATE(id));
  return res.data;
};

/** Stores an image (PNG, JPEG or WEBP, up to 10 MB); resolves with the `pazia-asset:` ref to put in the template. */
const uploadAsset = async (file: Blob, fileName: string): Promise<string> => {
  // No Content-Type header on purpose: the browser must add the multipart boundary itself.
  const body = new FormData();
  body.append("file", file, fileName);
  const res = await api.post<{ ref: string }>(endpoints.DOCUMENT_TEMPLATES_V2.ASSETS, body);
  return res.data.ref;
};

/** Downloads a template asset and returns it as a `data:` URL, which is what pdfme image fields take. */
const fetchAssetDataUrl = async (ref: string): Promise<string> => {
  const res = await api.get<ArrayBuffer>(endpoints.DOCUMENT_TEMPLATES_V2.ASSET(ref), { responseType: "arraybuffer" });
  return toDataUrl(res.data, mediaTypeOf(res.headers));
};

const createCourseDocument = async (courseId: number, payload: CourseDocumentV2Payload): Promise<number> => {
  const res = await api.post<number>(endpoints.COURSE_DOCUMENTS_V2.CREATE(courseId), payload);
  return res.data;
};

const updateCourseDocument = async (
  courseId: number,
  courseDocumentId: number,
  payload: CourseDocumentV2Payload
): Promise<void> => {
  await api.put(endpoints.COURSE_DOCUMENTS_V2.UPDATE(courseId, courseDocumentId), payload);
};

/**
 * The students of a v2 course document with their variables, photo flag and issue. Read-only: never allocates a number.
 * An empty `studentIds` asks for the whole course (the API's "no ids" meaning); `ids=` is never sent empty.
 */
const getStudents = async (
  courseId: number,
  courseDocumentId: number,
  studentIds: number[]
): Promise<CourseDocumentV2Student[]> => {
  const res = await api.get<CourseDocumentV2Student[]>(endpoints.COURSE_DOCUMENTS_V2.STUDENTS(courseId, courseDocumentId), {
    params: studentIds.length > 0 ? { ids: studentIds.join(",") } : {},
  });
  return res.data;
};

/**
 * Allocates (idempotently) the document numbers of the given students. The API's answer is not needed: read the issues
 * back with {@link getStudents}. With no students it does nothing, the API refuses an empty list.
 */
const ensureIssues = async (courseId: number, courseDocumentId: number, studentIds: number[]): Promise<void> => {
  if (studentIds.length === 0) return;
  await api.post(endpoints.COURSE_DOCUMENTS_V2.ISSUES(courseId, courseDocumentId), { studentIds });
};

/** The student's photo (PNG or JPEG) as a `data:` URL, or null when the student has no usable photo (404). */
const fetchStudentPhotoDataUrl = async (
  courseId: number,
  courseDocumentId: number,
  studentId: number
): Promise<string | null> => {
  try {
    const res = await api.get<ArrayBuffer>(endpoints.COURSE_DOCUMENTS_V2.STUDENT_PHOTO(courseId, courseDocumentId, studentId), {
      responseType: "arraybuffer",
    });
    return toDataUrl(res.data, mediaTypeOf(res.headers));
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

/**
 * Stores the PDF generated in the browser for one student, replacing the student's previous file; resolves with the new
 * file id. `inputsJson` is the student's own pdfme inputs (a JSON object of strings): blank or null leaves the saved
 * inputs untouched, send `"{}"` to clear them.
 */
const uploadStudentFile = async (
  courseId: number,
  courseDocumentId: number,
  studentId: number,
  pdf: Uint8Array,
  inputsJson: string | null
): Promise<number> => {
  const body = new FormData();
  // The cast: TS 5.7+ types a Uint8Array as possibly SharedArrayBuffer-backed, which BlobPart rejects.
  body.append("file", new Blob([pdf as BlobPart], { type: "application/pdf" }), "document.pdf");
  if (inputsJson && inputsJson.trim() !== "") body.append("inputsJson", inputsJson);
  const res = await api.post<number>(endpoints.COURSE_DOCUMENTS_V2.STUDENT_FILE(courseId, courseDocumentId, studentId), body);
  return res.data;
};

/** The PDF bytes of an extra page uploaded to a (v1 or v2) course document. */
const fetchExtraPage = async (courseId: number, courseDocumentId: number, pageId: number): Promise<ArrayBuffer> => {
  const res = await api.get<ArrayBuffer>(endpoints.COURSE_DOCUMENTS.EXTRA_PAGE_FILE(courseId, courseDocumentId, pageId), {
    responseType: "arraybuffer",
  });
  return res.data;
};

export const documentsV2Api = {
  list,
  get,
  create,
  update,
  remove,
  duplicate,
  uploadAsset,
  fetchAssetDataUrl,
  createCourseDocument,
  updateCourseDocument,
  getStudents,
  ensureIssues,
  fetchStudentPhotoDataUrl,
  uploadStudentFile,
  fetchExtraPage,
};
